import type { FilterConditionGroup, FilterParams } from "epsm-api-types";
import type { Security } from "epsm-api-types/models";
import { escapeRegex } from "@/lib/utils";

type SingleFilter = Exclude<
	FilterParams[number],
	{ group: FilterConditionGroup; params: FilterParams }
>;

type RegexFilter = {
	name: string;
	condition: "regex";
	value: unknown;
	options: string;
};

function applyFilter(item: Security, filter: SingleFilter) {
	const value = item[filter.name];

	switch (filter.condition) {
		case "eq":
			return value === filter.value;
		case "regex":
			return new RegExp(
				filter.value as string,
				(filter as RegexFilter).options,
			).test(value as string);
	}
	return false;
}

export const sortAndFilterSecurities = (
	securities: Security[],
	text: string,
	offset: number,
	limit: number,
) => {
	if (!securities.length) return [];

	const maxSize = offset + limit;
	const computedText = escapeRegex(text ?? "");

	const filterOptions: FilterParams[] = [
		[
			{
				name: "ticker",
				condition: "regex",
				value: `^${computedText}$`,
				options: "i",
			},
		],
		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "ticker",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: true },
		],
		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "ticker",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: false },
		],
		[
			{
				name: "ticker",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
			{ name: "etf", condition: "eq", value: true },
		],
		[
			{
				name: "ticker",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
		],

		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "name",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: true },
		],
		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "name",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: false },
		],
		[
			{
				name: "name",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
			{ name: "etf", condition: "eq", value: true },
		],
		[
			{
				name: "name",
				condition: "regex",
				value: `^${computedText}.*`,
				options: "i",
			},
		],

		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "ticker",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: true },
		],
		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "ticker",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: false },
		],
		[
			{
				name: "ticker",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
			{ name: "etf", condition: "eq", value: true },
		],
		[
			{
				name: "ticker",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
		],

		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "name",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: true },
		],
		[
			{ name: "code", condition: "eq", value: "EQS" },
			{
				name: "name",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
			{ name: "primary_listing", condition: "eq", value: false },
		],
		[
			{
				name: "name",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
			{ name: "etf", condition: "eq", value: true },
		],
		[
			{
				name: "name",
				condition: "regex",
				value: `^.+${computedText}.*`,
				options: "i",
			},
		],
	];

	const result = [];
	const seen = new Set();

	for (const filterGroup of filterOptions) {
		const filteredItems = securities.filter((item) =>
			filterGroup.every((filter) => applyFilter(item, filter as SingleFilter)),
		);

		for (const item of filteredItems) {
			if (!seen.has(item.ticker)) {
				seen.add(item.ticker);
				result.push(item);
				if (result.length > maxSize) {
					return result.slice(offset);
				}
			}
		}
	}

	return result.slice(offset);
};
